import { useCallback, useEffect, useState } from "react";

/**
 * Calls the given fetch call, and then calls it again at the given interval. 'loading' will only be true until the
 * first call is completed.
 *
 * @param fetchData The fetch call to make initially and then at the intervals.
 * @param intervalMs The interval length in milliseconds.
 */
export const useAutoRefreshingData = <T>(
  fetchData: () => Promise<T>,
  intervalMs = 15 * 60 * 1000 // 15 Minutes
) => {
  const [data, setData] = useState<T | undefined>(undefined);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(true);
  const [shouldRetry, setRetry] = useState<boolean>(true);

  const refresh = useCallback(() => setRetry(true), [setRetry]);

  useEffect(() => {
    const getData = async () => {
      setLoading(true);
      setError(undefined);
      try {
        const result = await fetchData();
        setData(result);
      } catch (err: any) {
        setError(err);
      } finally {
        setLoading(false);
        setRetry(false);
      }
    };
    if (shouldRetry) {
      getData();
    }

    const intervalId = setInterval(getData, intervalMs);

    return () => clearInterval(intervalId);
  }, [fetchData, intervalMs, shouldRetry]);

  return { data, error, loading, refresh };
};
